import type { Card } from '@ccc/shared-types';
import { cn } from '../lib/classnames';

interface CardTileProps {
  card: Card;
  selected?: boolean;
  onPick?: () => void;
  showLong?: boolean;
  size?: 'sm' | 'md' | 'lg';
  rarity?: 'common' | 'rare' | 'legendary';
}

/**
 * The actual card tile used in draft + game side panel + browse.
 * Visually distinguishes itself from Panel by having a colored rim and
 * a category label like a TCG card.
 */
export function CardTile({
  card,
  selected,
  onPick,
  showLong = true,
  size = 'md',
  rarity = 'common',
}: CardTileProps) {
  const rimVar = `--c-card-rim-${rarity}`;
  const dims = {
    sm: 'w-44 h-60',
    md: 'w-56 h-80',
    lg: 'w-64 h-96',
  }[size];
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'group relative text-left rounded-card overflow-hidden bg-surface shadow-card transition border',
        dims,
        selected
          ? 'ring-2 ring-accent shadow-card-hover scale-[1.02]'
          : 'hover:-translate-y-1 hover:shadow-card-hover',
        onPick ? 'cursor-pointer' : 'cursor-default',
        'focus-ring',
      )}
      style={{ borderColor: `rgb(var(${rimVar}) / 0.45)` }}
    >
      <div
        className="h-2 w-full"
        style={{ background: `rgb(var(${rimVar}))` }}
        aria-hidden
      />
      <div className="p-4 h-full flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
          {card.category}
        </div>
        <h3
          className={cn(
            'mt-1 font-display font-bold leading-tight text-ink',
            size === 'sm' ? 'text-lg' : size === 'lg' ? 'text-2xl' : 'text-xl',
          )}
        >
          {card.shortTitle}
        </h3>
        <div className="mt-3 h-px w-12 bg-ink/15" />
        {showLong && (
          <p className="mt-3 text-sm text-ink-muted leading-snug flex-1 overflow-y-auto pr-1">
            {card.description}
          </p>
        )}
        {card.flavor && (
          <p className="mt-2 text-xs italic text-ink-muted/80">{card.flavor}</p>
        )}
      </div>
    </button>
  );
}
